import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { DsBadge } from "../../../../design-system";
import {
  formatHistoryStatus,
  historyStatusTone,
  loadEssRequestHistory,
} from "../../../../lib/essRequestHistory";
import { useIsPhone } from "../../../../hooks/useIsPhone";
import { useBackToClose } from "../../shell/MobileSheet";

export const DS_QUICK_FIELD_CLS =
  "mt-1.5 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] outline-none transition focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/20";

/** Phone: request dialogs dock to the bottom as full-width sheets (backdrop + panel). */
export const PHONE_SHEET_BACKDROP_CLS = "max-md:items-end max-md:!p-0";
export const PHONE_SHEET_PANEL_CLS =
  "max-md:max-h-[92dvh] max-md:max-w-none max-md:rounded-b-none max-md:rounded-t-3xl max-md:border-x-0 max-md:border-b-0 max-md:pb-[env(safe-area-inset-bottom)] max-md:animate-in max-md:slide-in-from-bottom max-md:duration-300 motion-reduce:animate-none";

/**
 * Phone back gesture / Escape closes the request form.
 * Returns `closeThen(fn)`: close, then run `fn` (e.g. navigate) without the back step undoing it.
 */
export function usePhoneFormBack(open, onClose) {
  const isPhone = useIsPhone();
  const active = open && isPhone;
  const closeThen = useBackToClose(active, onClose);
  return (fn) => {
    if (active) return closeThen(fn);
    onClose?.();
    fn?.();
  };
}

export function useEssRequestHistory({ enabled, actionLabel, employeeId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadEssRequestHistory({ actionLabel, employeeId })
      .then((list) => {
        if (!cancelled) setRows(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, actionLabel, employeeId]);

  return { open, setOpen, rows, loading };
}

export function RequestFormFooter({
  historyCount = 0,
  historyLoading = false,
  onViewHistory,
  onCancel,
  submitDisabled = false,
  isSubmitting = false,
  submitLabel = "Submit Request",
  cancelDisabled = false,
}) {
  return (
    // Phone: pinned to the bottom of the scrolling form; -bottom-4 + pb-4 cancel the form's 1rem bottom padding.
    <div className="flex min-h-[40px] flex-wrap items-center gap-x-3 gap-y-2 border-t border-[hsl(var(--ds-border))] pt-4 max-md:sticky max-md:-bottom-4 max-md:z-[1] max-md:bg-[hsl(var(--ds-surface))] max-md:pb-4">
      <div className="flex min-w-[133px] flex-1 items-center justify-start max-md:basis-full">
        <button
          type="button"
          onClick={onViewHistory}
          className="inline-flex h-9 items-center justify-center rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 text-xs font-medium text-[hsl(var(--ds-fg))] transition hover:opacity-90 max-md:h-10 max-md:w-full max-md:text-sm"
        >
          View history
          {!historyLoading && historyCount > 0 ? ` (${historyCount})` : ""}
        </button>
      </div>
      <div className="flex min-w-[200px] flex-1 justify-end gap-2 max-md:basis-full">
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelDisabled || isSubmitting}
          className="inline-flex h-9 items-center justify-center rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 text-xs font-medium text-[hsl(var(--ds-fg))] transition hover:bg-[hsl(var(--ds-muted))] disabled:opacity-50 max-md:h-11 max-md:flex-1 max-md:text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitDisabled || isSubmitting}
          className="inline-flex h-9 items-center justify-center rounded-xl border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))] px-4 text-xs font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60 max-md:h-11 max-md:flex-[2] max-md:whitespace-nowrap max-md:text-sm"
        >
          {isSubmitting ? "Submitting…" : submitLabel}
        </button>
      </div>
    </div>
  );
}

export function RequestHistoryOverlay({
  open,
  onClose,
  title,
  subtitle,
  rows = [],
  loading = false,
  emptyMessage,
}) {
  usePhoneFormBack(open, onClose);
  if (!open) return null;
  const overlay = (
    <div
      className={`fixed inset-0 z-[12000] flex items-center justify-center bg-black/50 p-4 ${PHONE_SHEET_BACKDROP_CLS}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={`flex max-h-[min(76vh,532px)] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)] ${PHONE_SHEET_PANEL_CLS}`}
        role="dialog"
        aria-labelledby="ess-request-history-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h3
              id="ess-request-history-title"
              className="text-base font-semibold text-[hsl(var(--ds-fg))]"
            >
              {title}
            </h3>
            {subtitle ? (
              <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
            aria-label="Close history"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading requests…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
              {emptyMessage || "No applications yet. Submit a request to add an entry here."}
            </p>
          ) : (
            <ul className="divide-y divide-[hsl(var(--ds-border))] rounded-xl border border-[hsl(var(--ds-border))]">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center gap-x-3 px-3 py-2.5 text-xs tabular-nums max-md:flex-wrap max-md:gap-y-1 max-md:py-3"
                >
                  <span className="min-w-0 shrink truncate font-semibold text-[hsl(var(--ds-fg))]">
                    {row.title}
                  </span>
                  {(row.details || []).map((d) => (
                    <span key={`${row.id}-${d.label}`} className="shrink-0">
                      <span className="font-medium text-[hsl(var(--ds-muted-fg))]">
                        {d.label}
                      </span>{" "}
                      {d.value}
                    </span>
                  ))}
                  <DsBadge
                    tone={historyStatusTone(row.status)}
                    className="ml-auto shrink-0 text-[10px]"
                  >
                    {formatHistoryStatus(row.status)}
                  </DsBadge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
  if (typeof document === "undefined") return overlay;
  return createPortal(overlay, document.body);
}
